import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { createSalesChannel } from '../redux/OrderCRUD';

interface ChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (channelData: { id: string; name: string; code: string }) => Promise<void>;
  loading?: boolean;
}

export default function ChannelDialog({
  open,
  onClose,
  onSave,
  loading = false,
}: ChannelDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Tên kênh bán hàng không được để trống');
      return;
    }

    try {
      setError(undefined);
      const response = await createSalesChannel({
        name: formData.name.trim(),
      });
      
      const newChannel = response.data;
      await onSave(newChannel);
      
      setSuccessMessage('Tạo kênh bán hàng thành công!');
      setFormData({ name: '' });
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
        setSuccessMessage(undefined);
      }, 1500);
      
    } catch (error: any) {
      setError(formatErrorForSnackbar(error) || 'Có lỗi xảy ra khi tạo kênh bán hàng');
    }
  };

  const handleClose = () => {
    setFormData({ name: '' });
    setError(undefined);
    setSuccessMessage(undefined);
    onClose();
  };

  const disabled = !formData.name.trim() || loading;

  const sections = React.useMemo<AppFormSection[]>(() => [
    {
      key: 'channel-basic',
      title: 'Thông tin kênh bán hàng',
      fields: [
        {
          key: 'name',
          label: 'Tên kênh bán hàng',
          type: 'text',
          required: true,
          placeholder: 'Nhập tên kênh bán hàng...',
          error: !!error,
          helperText: error,
          gridSize: { xs: 12 },
        },
      ],
    },
  ], [error]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Tạo kênh bán hàng mới</DialogTitle>
        <DialogContent sx={{ mt: 2, px: 2, pb: 2, overflow: 'auto' }}>
          <AppForm
            sections={sections}
            values={formData as Record<string, any>}
            onChange={(key, value) => handleInputChange(key as keyof typeof formData, value)}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitting={loading}
            submitDisabled={disabled}
            submitLabel="Tạo kênh bán hàng"
            submittingLabel="Đang tạo..."
            cancelLabel="Hủy"
            containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
          />
        </DialogContent>
      </Dialog>

      {successMessage && (
        <MessageSnackbar
          open={true}
          message={successMessage}
          severity="success"
          onClose={() => setSuccessMessage(undefined)}
        />
      )}
    </>
  );
}
